// Bio Page built-in buttons (Book Online, Our Services, Give a friend, Get Directions, Call Us, Text Us)
// and the auto campaign buttons. Pure rules shared by the API schema, the Bio Page editor and the public renderer
// so all three agree on ids, default order, insert position and the campaign button defaults.

import type { BookingLinkAction } from './booking-link-action';
import type { LinkBioAttentionEffect } from './link-bio-button-options';
import { fnv1aHash32 } from './fnv1a-hash';
import { parseCalendarDateParts } from './calendar-date-parts';

/** Every button in `draftConfig.buttons` has one of these kinds; `link` is a salon-made custom button. */
export const LINK_BIO_BUTTON_KINDS = ['link', 'booking', 'services', 'giveFriend', 'directions', 'call', 'text'] as const;
export type LinkBioButtonKind = (typeof LINK_BIO_BUTTON_KINDS)[number];

/** The buttons every page carries, in default order. Toggle-only: never deleted, still draggable. */
export const LINK_BIO_BUILT_IN_BUTTON_KINDS = ['booking', 'services', 'giveFriend', 'directions', 'call', 'text'] as const;
export type LinkBioBuiltInButtonKind = (typeof LINK_BIO_BUILT_IN_BUTTON_KINDS)[number];

/** Fixed ids, one per built-in kind. A custom `link` button may never use one. */
export const LINK_BIO_BUILT_IN_BUTTON_IDS = {
  booking: 'builtin-booking',
  services: 'builtin-services',
  giveFriend: 'builtin-give-friend',
  directions: 'builtin-directions',
  call: 'builtin-call',
  text: 'builtin-text',
} as const satisfies Record<LinkBioBuiltInButtonKind, string>;

export type LinkBioBuiltInButtonId =
  (typeof LINK_BIO_BUILT_IN_BUTTON_IDS)[LinkBioBuiltInButtonKind];

/** Default label, icon and style per built-in. Book Online is the only Filled one. */
export const LINK_BIO_BUILT_IN_BUTTON_DEFAULTS = {
  booking: { label: 'Book Online', icon: 'calendar', styleVariant: 'solid' },
  services: { label: 'Our Services', icon: 'scissors', styleVariant: 'outline' },
  giveFriend: { label: 'Give a friend', icon: 'gift', styleVariant: 'outline' },
  directions: { label: 'Get Directions', icon: 'location', styleVariant: 'outline' },
  call: { label: 'Call Us', icon: 'phone', styleVariant: 'outline' },
  text: { label: 'Text Us', icon: 'message', styleVariant: 'outline' },
} as const satisfies Record<
  LinkBioBuiltInButtonKind,
  { label: string; icon: string; styleVariant: 'solid' | 'outline' }
>;

/** A restaurant's Book Online button: its default words and icon when the link is the restaurant's ordering site. */
export const LINK_BIO_ORDER_ONLINE_LABEL = 'Order Online';
export const LINK_BIO_ORDER_ONLINE_ICON = 'order';

/**
 * What the Book Online button says: a restaurant's still-default label reads "Order Online"; a label the owner typed
 * stays as typed. Swapped at read time, so nothing saved changes when the salon's type or link does.
 */
export function linkBioBookingButtonLabel(savedLabel: string, action: BookingLinkAction): string {
  const isDefault = savedLabel === LINK_BIO_BUILT_IN_BUTTON_DEFAULTS.booking.label;
  return isDefault && action === 'ORDER' ? LINK_BIO_ORDER_ONLINE_LABEL : savedLabel;
}

/** {@link linkBioBookingButtonLabel} for the icon: the calendar reads as the bag at a restaurant, any other icon stays. */
export function linkBioBookingButtonIcon<T extends string | null | undefined>(savedIcon: T, action: BookingLinkAction): T | typeof LINK_BIO_ORDER_ONLINE_ICON {
  const isDefault = savedIcon === LINK_BIO_BUILT_IN_BUTTON_DEFAULTS.booking.icon;
  return isDefault && action === 'ORDER' ? LINK_BIO_ORDER_ONLINE_ICON : savedIcon;
}

/** Our Services opens the Hayven price page or a link the salon pastes. */
export const LINK_BIO_SERVICES_DESTINATIONS = ['hayven', 'url'] as const;
export type LinkBioServicesDestination = (typeof LINK_BIO_SERVICES_DESTINATIONS)[number];

/** Get Directions opens a Google Maps route or the salon's Google Business page. */
export const LINK_BIO_DIRECTIONS_DESTINATIONS = ['maps', 'gbp'] as const;
export type LinkBioDirectionsDestination = (typeof LINK_BIO_DIRECTIONS_DESTINATIONS)[number];

/** Call Us dials the salon's primary location phone, or another number the salon types. */
export const LINK_BIO_CALL_PHONE_SOURCES = ['salon', 'custom'] as const;
export type LinkBioCallPhoneSource = (typeof LINK_BIO_CALL_PHONE_SOURCES)[number];

/** Cap on the salon's own `link` buttons per page; built-ins never count towards it. */
export const LINK_BIO_CUSTOM_BUTTONS_MAX = 12;

/** Text Us prefilled message cap: kept short so it fits the visitor's message box. */
export const LINK_BIO_SMS_BODY_MAX = 160;

/** Per-campaign overrides a page can store (one per campaign). */
export const LINK_BIO_CAMPAIGN_BUTTONS_MAX = 50;

/** Effects a live campaign button gets by default — every looping effect, never 'none'. */
export const LINK_BIO_CAMPAIGN_ATTENTION_EFFECTS = [
  'shake',
  'bounce',
  'pulse',
  'shine',
] as const satisfies readonly LinkBioAttentionEffect[];

export function isLinkBioBuiltInButtonId(value: string): value is LinkBioBuiltInButtonId {
  return (Object.values(LINK_BIO_BUILT_IN_BUTTON_IDS) as string[]).includes(value);
}

/**
 * Where a new custom button goes: directly above Get Directions (wherever the salon dragged it),
 * else above Call Us, else at the end. A Give a friend sitting right there stays below the new button,
 * so custom links keep their place above it. Returns a new list in display order with sortOrder 0..n-1.
 */
export function insertLinkBioButtonBeforeDirections<T extends { kind?: string; sortOrder: number }>(
  buttons: readonly T[],
  newButton: T
): T[] {
  const ordered = [...buttons].sort((left, right) => left.sortOrder - right.sortOrder);
  const anchor = ['directions', 'call']
    .map((kind) => ordered.findIndex((button) => button.kind === kind))
    .find((index) => index >= 0);
  const end = anchor ?? ordered.length;
  const insertAt = ordered[end - 1]?.kind === 'giveFriend' ? end - 1 : end;
  ordered.splice(insertAt, 0, newButton);
  return ordered.map((button, sortOrder) => ({ ...button, sortOrder }));
}

/**
 * Stable "random" effect for a campaign button: FNV-1a hash of the campaign id, so the editor and
 * the public page always agree and the effect never changes between page loads.
 */
export function pickCampaignAttentionEffect(campaignId: string): LinkBioAttentionEffect {
  const hash = fnv1aHash32(campaignId);
  return (
    LINK_BIO_CAMPAIGN_ATTENTION_EFFECTS[hash % LINK_BIO_CAMPAIGN_ATTENTION_EFFECTS.length] ?? 'shake'
  );
}

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Short calendar date, e.g. `2026-10-05` → `Oct 5`. Reads the calendar parts directly (never through
 * `Date`) so no timezone can shift the day. Unreadable or impossible input (Feb 31) → ''.
 */
export function formatCampaignShortDate(isoDate: string): string {
  const parts = parseCalendarDateParts(isoDate, { exact: false });
  return parts ? `${MONTH_ABBREVIATIONS[parts.month - 1]} ${parts.day}` : '';
}

/** Default campaign sub-text from its end date, e.g. `2026-10-05` → `Ends Oct 5`. Unreadable input → ''. */
export function formatCampaignEndsSubText(endIsoDate: string): string {
  const date = formatCampaignShortDate(endIsoDate);
  return date ? `Ends ${date}` : '';
}
